// Global Support modal context — `useSupport()` exposes
// `{ openSupport, closeSupport, isOpen }` to any component. The
// provider mounts the SupportModal at the App root so the modal
// floats over every route. Trigger button in DesktopSidebar.js.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import SupportModal from "../components/support/SupportModal";

const SupportContext = createContext(null);

export function SupportProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialTab, setInitialTab] = useState("report"); // 'report' | 'faq'

  const openSupport = useCallback((tab = "report") => {
    setInitialTab(tab === "faq" ? "faq" : "report");
    setIsOpen(true);
  }, []);
  const closeSupport = useCallback(() => setIsOpen(false), []);

  // ESC key closes the modal — keyboard accessibility.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeSupport(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeSupport]);

  return (
    <SupportContext.Provider value={{ isOpen, openSupport, closeSupport, initialTab }}>
      {children}
      <SupportModal />
    </SupportContext.Provider>
  );
}

export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) {
    // Soft fallback so legacy renders that don't have the provider
    // mounted yet don't crash on import. Buttons that import this
    // hook get a no-op `openSupport`.
    return { isOpen: false, openSupport: () => {}, closeSupport: () => {}, initialTab: "report" };
  }
  return ctx;
}
